<script setup lang="ts">
import { computed, ref, watch } from 'vue';

interface Props {
  handlers: {
    compositionstart: () => void;
    compositionend: () => void;
    keydown: (event: KeyboardEvent) => Promise<void> | void;
    input?: (event: Event) => void;
    blur?: (event: FocusEvent) => void;
    focus?: () => void;
  };
  maxLength?: number;
  placeholder?: string;
}

const props = defineProps<Props>();
const modelValue = defineModel<string>({
  required: true,
});

const textareaDummyContent = ref<string>('');

const generateTextareaDummyContent = (value: string) => {
  // white-space: pre-wrap; はテキスト末尾の改行を表示しないため、末尾にゼロ幅スペースを追加する
  return value + '\u200b';
};
const adjustTextareaHeight = (event: Event) => {
  if (!event.target) return;
  textareaDummyContent.value = generateTextareaDummyContent(
    (event.target as HTMLTextAreaElement).value,
  );
};

// `input` 以外のイベントをバインド
const filteredHandlers = computed(() => {
  const { input, ...otherHandlers } = props.handlers;
  return otherHandlers;
});

// input イベント時の処理
const handleInput = (event: Event) => {
  modelValue.value = (event.target as HTMLTextAreaElement).value;
  adjustTextareaHeight(event);
  props.handlers.input?.(event);
};

watch(
  () => modelValue.value,
  newModelValue => {
    textareaDummyContent.value = generateTextareaDummyContent(newModelValue);
  },
  { immediate: true },
);
</script>

<template>
  <div class="input-section">
    <div class="textarea-dummy" aria-hidden="true">
      {{ textareaDummyContent }}
    </div>
    <slot :handle-input="handleInput" :filtered-handlers="filteredHandlers">
      <textarea
        class="c-text-regular--m c-textArea"
        :value="modelValue"
        @input="handleInput"
        v-on="filteredHandlers"
        :maxlength="maxLength"
        :placeholder="placeholder"
      />
    </slot>
  </div>
</template>

<style lang="scss" scoped>
.input-section {
  position: relative;
  line-height: 23px;
  :deep(textarea) {
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    width: 100%;
    height: 100%;
    overflow: hidden;
    box-sizing: border-box;
    padding: 6px 12px;
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-wrap: break-word;
    background-color: transparent;
    color: inherit;
    resize: none;
    outline: none;
  }
  .textarea-dummy {
    overflow: hidden;
    visibility: hidden;
    box-sizing: border-box;
    padding: 6px 12px;
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
}
</style>
